import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, query, where, addDoc } from "firebase/firestore";
import {
  Container, TextField, Button, Select, MenuItem, InputLabel, FormControl,
  Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Alert, Modal, Box
} from "@mui/material";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import RegistroUnidadTipoUnidad from "./RegistroUnidadTipoUnidad"; // Asegúrate que esté en la misma ruta

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

const Cotizacion = () => {
  const [empresas, setEmpresas] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [imagenes, setImagenes] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  const [formData, setFormData] = useState({
    empresaId: "",
    unidadId: "",
    serviciosSeleccionados: [],
    encargadoId: "",
    mecanicoId: "",
  });

  const [alert, setAlert] = useState({ message: "", severity: "" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const empresasSnap = await getDocs(collection(db, "empresas"));
        const serviciosSnap = await getDocs(collection(db, "servicios"));
        const contactosSnap = await getDocs(collection(db, "contactos"));

        setEmpresas(empresasSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setServicios(serviciosSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setContactos(contactosSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error al obtener datos:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchUnidades = async () => {
      if (!formData.empresaId) {
        setUnidades([]);
        return;
      }

      try {
        const unidadesQuery = query(
          collection(db, "unidades"),
          where("id_empresa", "==", formData.empresaId)
        );
        const unidadesSnap = await getDocs(unidadesQuery);
        setUnidades(unidadesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error al obtener unidades:", error);
      }
    };

    fetchUnidades();
  }, [formData.empresaId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddService = () => {
    setFormData(prev => ({
      ...prev,
      serviciosSeleccionados: [...prev.serviciosSeleccionados, { servicioId: "", cantidad: 1, precio: 0 }]
    }));
  };

  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...formData.serviciosSeleccionados];
    if (field === "servicioId") {
      const servicio = servicios.find(s => s.id === value);
      updatedServices[index].precio = servicio ? servicio.precio : 0;
    }
    updatedServices[index][field] = value;
    setFormData(prev => ({ ...prev, serviciosSeleccionados: updatedServices }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImagenes(files);
  };

  const subtotal = formData.serviciosSeleccionados.reduce(
    (acc, servicio) => acc + servicio.precio * servicio.cantidad,
    0
  );
  const iva = subtotal * 0.16;
  const total = subtotal + iva;

  const handleSubmit = async () => {
    if (unidades.length === 0 && !formData.unidadId) {
      setAlert({ message: "No hay unidades registradas. Por favor, registre una unidad o solicite un servicio.", severity: "warning" });
      return;
    }

    if (formData.serviciosSeleccionados.length === 0) {
      setAlert({ message: "Debe seleccionar al menos un servicio.", severity: "error" });
      return;
    }

    try {
      await addDoc(collection(db, "cotizaciones"), { ...formData, subtotal, iva, total });
      setAlert({ message: "Cotización guardada correctamente", severity: "success" });
    } catch (error) {
      setAlert({ message: `Error al guardar la cotización: ${error.message}`, severity: "error" });
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.text("Cotización", 20, 20);

    doc.autoTable({
      head: [["Servicio", "Cantidad", "Precio Unitario", "Subtotal"]],
      body: formData.serviciosSeleccionados.map(s => {
        const servicio = servicios.find(serv => serv.id === s.servicioId) || {};
        const precio = Number(s.precio) || 0;
        const cantidad = Number(s.cantidad) || 0;
        return [
          servicio.nombre || "Desconocido",
          cantidad,
          `$${precio.toFixed(2)}`,
          `$${(precio * cantidad).toFixed(2)}`
        ];
      }),
      startY: 30,
    });

    const yPosition = doc.autoTable.previous.finalY + 10;
    doc.text(`Subtotal: $${subtotal.toFixed(2)}`, 20, yPosition);
    doc.text(`IVA: $${iva.toFixed(2)}`, 20, yPosition + 10);
    doc.text(`Total: $${total.toFixed(2)}`, 20, yPosition + 20);
    doc.save("cotizacion.pdf");
  };

  const encargados = contactos.filter(c => c.rol === "Encargado");
  const mecanicos = contactos.filter(c => c.rol === "Mecanico");

  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom>Cotización</Typography>

      {alert.message && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>{alert.message}</Alert>
      )}

      <FormControl fullWidth margin="normal">
        <InputLabel>Empresa</InputLabel>
        <Select name="empresaId" value={formData.empresaId} onChange={handleChange}>
          {empresas.map(emp => <MenuItem key={emp.id} value={emp.id}>{emp.nombre}</MenuItem>)}
        </Select>
      </FormControl>

      {unidades.length > 0 ? (
        <FormControl fullWidth margin="normal">
          <InputLabel>Unidad</InputLabel>
          <Select name="unidadId" value={formData.unidadId} onChange={handleChange}>
            {unidades.map(uni => (
              <MenuItem key={uni.id} value={uni.id}>
                {uni.numero}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <div>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            No hay unidades registradas para esta empresa.
          </Typography>
          <Button variant="outlined" onClick={() => setOpenModal(true)}>Registrar Unidad</Button>
        </div>
      )}

      <FormControl fullWidth margin="normal">
        <InputLabel>Encargado</InputLabel>
        <Select name="encargadoId" value={formData.encargadoId} onChange={handleChange}>
          {encargados.map(contacto => (
            <MenuItem key={contacto.id} value={contacto.id}>{contacto.nombre}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel>Mecánico</InputLabel>
        <Select name="mecanicoId" value={formData.mecanicoId} onChange={handleChange}>
          {mecanicos.map(contacto => (
            <MenuItem key={contacto.id} value={contacto.id}>{contacto.nombre}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button onClick={handleAddService} variant="contained" color="secondary">Agregar Servicio</Button>

      <TableContainer component={Paper} sx={{ my: 2 }} maxWidth="md">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Servicio</TableCell>
              <TableCell>Cantidad</TableCell>
              <TableCell>Precio</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formData.serviciosSeleccionados.map((serv, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Select
                    value={serv.servicioId}
                    onChange={e => handleServiceChange(index, "servicioId", e.target.value)}
                  >
                    <MenuItem value="" disabled>Selecciona un servicio...</MenuItem>
                    {servicios.map(s => (
                      <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={serv.cantidad}
                    onChange={e => handleServiceChange(index, "cantidad", Number(e.target.value))}
                  />
                </TableCell>
                <TableCell>${serv.precio}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button variant="contained" component="label">
        Subir Imágenes
        <input hidden multiple type="file" accept="image/*" onChange={handleImageUpload} />
      </Button>

      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {imagenes.map((img, i) => (
          <img
            key={i}
            src={URL.createObjectURL(img)}
            alt={`img-${i}`}
            style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }}
          />
        ))}
      </Box>

      <Typography variant="h6" sx={{ mt: 2 }}>Total: ${total.toFixed(2)}</Typography>

      <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ mt: 2 }}>Guardar</Button>
      <Button onClick={generatePDF} variant="contained" color="secondary" sx={{ mt: 2, ml: 2 }}>Generar PDF</Button>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={style}>
          <Typography variant="h6" gutterBottom>Registro de Unidad</Typography>
          <RegistroUnidadTipoUnidad onClose={() => setOpenModal(false)} />
        </Box>
      </Modal>
    </Container>
  );
};

export default Cotizacion;
